import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { push } from '../../services/router-utils';
import _ from 'lodash';
import { openModal } from '../../actions/open-modal';
import { startEditingComment } from '../../actions/start-editing-comment';
import { buttonClicked } from '../../actions/button-clicked';
import { actionsOpened } from '../../actions/actions-opened';
import { actionsClosed } from '../../actions/actions-closed';
import { addTopComment } from '../../actions/add-top-comment';
import { removeTopComment } from '../../actions/remove-top-comment';
import { DeleteIcon, ShareIcon, ReportIcon, EditIcon, TopCommentIcon } from '../../components/icons';
import ActionButton from '../../components/action-button';
import {
  MODAL_TYPE_SHARE_COMMENT,
  MODAL_TYPE_REPORT_COMMENT,
  MODAL_TYPE_DELETE_COMMENT,
} from '../../constants/modal-types';
import withTranslate from '../../hoc/with-translate';
import withPermissions from '../../hoc/with-permissions';
import withDeviceType from '../../hoc/with-device-type';
import withEnvironment from '../../hoc/with-environment';
import withAuth from '../../hoc/with-auth';
import messages, {
  POST_ADD_TOP_COMMENT,
  POST_REMOVE_TOP_COMMENT,
} from '../../components/message/messages';
import { getPost } from '../../selectors/post-selectors';
import { getCategory } from '../../selectors/categories-selectors';
import Wix from '../../services/wix-sdk-polyfill';

const showShare = () => {
  return Wix.Utils.getViewMode() !== 'editor' && Wix.Utils.getViewMode() !== 'preview';
};

export class CommentActions extends Component {
  openModal = type => {
    return () => {
      const { buttonClicked, openModal, focusOnCloseId, category, post, comment } = this.props;
      buttonClicked({ action: type });

      return openModal(type, {
        categoryId: category._id,
        categorySlug: category.slug,
        postId: post._id,
        postSlug: post.slug,
        commentId: comment._id,
      }, focusOnCloseId);
    };
  };

  handleEdit = () => {
    const { isMobile, push, startEditingComment, comment, category, post } = this.props;

    if (isMobile) {
      push(`/${category.slug}/${post.slug}/edit-comment/${comment._id}`);
    } else {
      startEditingComment(comment._id);
    }
  };

  handleAddTopComment = () => {
    const { post, comment, buttonClicked, addTopComment, showMessage } = this.props;
    buttonClicked({ action: 'add-top-comment' });
    addTopComment(post._id, comment._id).then(
      () => showMessage(messages[POST_ADD_TOP_COMMENT].content(), messages[POST_ADD_TOP_COMMENT].type));
  }

  handleRemoveTopComment = () => {
    const { post, buttonClicked, removeTopComment, showMessage } = this.props;
    buttonClicked({ action: 'remove-top-comment' });
    removeTopComment(post._id).then(
      () => showMessage(messages[POST_REMOVE_TOP_COMMENT].content(), messages[POST_REMOVE_TOP_COMMENT].type));
  }

  componentDidMount() {
    this.props.actionsOpened({ type: 'comment' });
  }

  componentWillUnmount() {
    this.props.actionsClosed({ type: 'comment' });
  }

  render() {
    const { canRender, comment, t, forPublicUser } = this.props;
    const shareButton =
      canRender('share', 'comment', comment, () => (
        <ActionButton data-hook="comment-actions__share" onClick={this.openModal(MODAL_TYPE_SHARE_COMMENT)}>
          <ShareIcon className="forum-icon-fill"/>
          {t('comment-actions.share-comment')}
        </ActionButton>
      ));
    const reportButton =
      canRender('report', 'comment', comment, can => (
        <ActionButton
          data-hook="comment-actions__report"
          onClick={can ? this.openModal(MODAL_TYPE_REPORT_COMMENT) : forPublicUser(this.openModal(MODAL_TYPE_REPORT_COMMENT))}
        >
          <ReportIcon className="forum-icon-fill"/>
          {t('comment-actions.report-comment')}
        </ActionButton>
      ));
    const editButton =
      canRender('edit', 'comment', comment, can => (
        <ActionButton
          data-hook="comment-actions__edit"
          onClick={can ? this.handleEdit : forPublicUser(this.handleEdit)} className="comment-actions__edit"
        >
          <EditIcon className="forum-icon-fill"/>
          {t('comment-actions.edit-comment')}
        </ActionButton>
      ));
    const deleteButton = canRender('delete', 'comment', comment, () => (
      <ActionButton data-hook="comment-actions__delete" onClick={this.openModal(MODAL_TYPE_DELETE_COMMENT)}>
        <DeleteIcon className="forum-icon-fill"/>
        {t('comment-actions.delete-comment')}
      </ActionButton>
    ));
    return (
      <div>
        {showShare() && shareButton}
        {this.renderTopCommentButton()}
        {reportButton}
        {editButton}
        {deleteButton}
      </div>
    );
  }

  renderTopCommentButton() {
    const { canRender, post, comment, t } = this.props;

    if (post.topCommentId !== comment._id) {
      return canRender('add-top-comment', 'post', post, () => (
        <ActionButton data-hook="comment-actions__add-top-comment" onClick={this.handleAddTopComment}>
          <TopCommentIcon className="forum-icon-fill"/>
          {t('comment-actions.add-top-comment')}
        </ActionButton>
      ));
    }

    return canRender('remove-top-comment', 'post', post, () => (
      <ActionButton data-hook="comment-actions__remove-top-comment" onClick={this.handleRemoveTopComment}>
        <TopCommentIcon className="forum-icon-fill"/>
        {t('comment-actions.remove-top-comment')}
      </ActionButton>
    ));
  }
}

CommentActions.propTypes = {
  comment: PropTypes.object.isRequired,
  post: PropTypes.object.isRequired,
  category: PropTypes.object.isRequired,
  currentUser: PropTypes.object,
  isOwner: PropTypes.bool,
  openModal: PropTypes.func.isRequired,
  startEditingComment: PropTypes.func.isRequired,
  buttonClicked: PropTypes.func.isRequired,
  actionsOpened: PropTypes.func.isRequired,
  actionsClosed: PropTypes.func.isRequired,
  addTopComment: PropTypes.func.isRequired,
  removeTopComment: PropTypes.func.isRequired,
  canRender: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  push: PropTypes.func.isRequired,
  isMobile: PropTypes.bool.isRequired,
  focusOnCloseId: PropTypes.string.isRequired,
  forPublicUser: PropTypes.func,
  showMessage: PropTypes.func,
};

const mapStateToProps = (state, ownProps) => {
  const comment = ownProps.comment;
  const post = getPost(state, comment.postId);
  const category = getCategory(state, post.categoryId);
  return { category, post };
};

const mapDispatchToProps = {
  openModal,
  startEditingComment,
  actionsOpened,
  actionsClosed,
  addTopComment,
  removeTopComment,
  buttonClicked: data => buttonClicked({ name: 'action_click', type: 'comment', ...data }),
  push,
};

export default _.flowRight(
  connect(mapStateToProps, mapDispatchToProps),
  withTranslate,
  withPermissions,
  withDeviceType,
  withEnvironment({
    showMessage: 'showMessage',
  }),
  withAuth,
)(CommentActions);



// WEBPACK FOOTER //
// ./app/containers/comment-actions/comment-actions.js